import React, { useState, useEffect } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendEmailVerification,
  signOut,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../utils/firebase-config';
import '../styles/login.css';
import TwoFactorSetup from './TwoFactorSetup';
import '../styles/twofa.css';

// InviteAccept - the page a GIDU invitation link lands on.
//
// A seat is never self-registered: anyone registering normally becomes a GIDO (their
// own org, bucket, free tier), so an employee self-registering and then being "granted
// a seat" would give one person two identities. D-024 keeps GIDO and GIDU distinct;
// invitation is what preserves that. This page is the only way a seat is created.
//
// TWO PATHS, one page:
//   new here        - they have no DBOS login. They set a password, the account is
//                     created client-side (same as LoginForm's registration), then the
//                     seat is attached.
//   already have one - they are a GIDO owner, or hold a seat elsewhere. They sign in
//                     with their existing password and the seat is added ALONGSIDE
//                     whatever they already have. One login, many seats - this is what
//                     makes the pharmacy-invites-their-accountant case work.
//
// The backend does the real check: the accepting account's own verified email must
// match the invited address. Holding the link is never enough. This page cannot
// weaken that - it only decides what to show.
//
// 2FA is mandatory platform-wide, so acceptance ends in TwoFactorSetup exactly as
// ordinary registration does. A seat exists but is not usable until that completes.

const ACCEPT_URL =
  'https://europe-west1-dbos-core-prod.cloudfunctions.net/gidu-accept';

const PORTAL_URL = 'https://portal.dbos.world';

function InviteAccept({ token }) {
  const [path, setPath] = useState(null);          // null | 'new' | 'existing'
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [accepted, setAccepted] = useState(null);  // the company they joined
  const [showTwoFactorSetup, setShowTwoFactorSetup] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [currentGido, setCurrentGido] = useState(null);

  // The invite's details are not readable before acceptance - the token is the only
  // thing we hold, and the backend will not describe an invite to an unauthenticated
  // caller (that would let anyone with a link learn who was invited where). So the
  // page asks for the email rather than showing it, and the backend confirms the match.
  useEffect(() => {
    if (!token) setError('This invitation link is incomplete.');
  }, [token]);

  async function callAccept(user) {
    const idToken = await user.getIdToken();
    const res = await fetch(ACCEPT_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({ token, name: fullName.trim() }),
    });
    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.message || data.error || 'This invitation could not be completed.');
    }
    return data;
  }

  async function loadGido(uid) {
    // A pure GIDU has no gido_registry record of their own. TwoFactorSetup takes a
    // gido prop, so pass whatever exists - null is acceptable for a seat-only user.
    try {
      const snap = await getDoc(doc(db, 'gido_registry', uid));
      return snap.exists() ? snap.data() : null;
    } catch (e) {
      return null;
    }
  }

  async function handleNewAccount(e) {
    e.preventDefault();
    setError('');

    if (!fullName.trim() || fullName.trim().length < 2) {
      setError('Please enter your full name.');
      return;
    }
    if (password.length < 8) {
      setError('Your password needs at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('The two passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const cred = await createUserWithEmailAndPassword(auth, email.trim().toLowerCase(), password);
      // LoginForm blocks any login until the address is verified. Without this, an
      // invited person accepts successfully and is then locked out at the front door.
      await sendEmailVerification(cred.user);
      const data = await callAccept(cred.user);
      setAccepted(data);
      setCurrentUser(cred.user);
      setCurrentGido(await loadGido(cred.user.uid));
      setShowTwoFactorSetup(true);
    } catch (err) {
      // If the account was created but acceptance failed, the account is left behind
      // with no seat. Sign out so a retry with the right address is clean rather than
      // silently half-logged-in.
      if (auth.currentUser) {
        try { await signOut(auth); } catch (_) { /* best effort */ }
      }
      if (err.code === 'auth/email-already-in-use') {
        setError('You already have a DBOS login with that address. Choose "I already have a DBOS login" above.');
      } else if (err.code === 'auth/invalid-email') {
        setError('That does not look like a valid email address.');
      } else if (err.code === 'auth/weak-password') {
        setError('That password is too weak. Use at least 8 characters.');
      } else {
        setError(err.message || 'Something went wrong. Please try again.');
      }
    }
    setLoading(false);
  }

  async function handleExistingAccount(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const cred = await signInWithEmailAndPassword(auth, email.trim().toLowerCase(), password);
      const data = await callAccept(cred.user);
      setAccepted(data);
      setCurrentUser(cred.user);
      setCurrentGido(await loadGido(cred.user.uid));
      // Someone who already had a DBOS login already has 2FA set up - they do not set
      // it up again, they simply carry on to the portal with their seat added.
      setShowTwoFactorSetup(false);
    } catch (err) {
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setError('That password is not right for this address.');
      } else if (err.code === 'auth/user-not-found') {
        setError('There is no DBOS login with that address. Choose "I am new to DBOS" above.');
      } else {
        setError(err.message || 'Something went wrong. Please try again.');
      }
    }
    setLoading(false);
  }

  function handle2FASetupComplete() {
    window.location.href = PORTAL_URL;
  }

  if (showTwoFactorSetup) {
    return (
      <TwoFactorSetup
        user={currentUser}
        gido={currentGido}
        onComplete={handle2FASetupComplete}
      />
    );
  }

  // Accepted, and no 2FA step needed (an existing DBOS login already has it).
  if (accepted && !showTwoFactorSetup) {
    return (
      <div className="login-container">
        <div className="login-form">
          <h2>You are in</h2>
          <p>
            Your seat at <strong>{accepted.company_name}</strong> is active.
          </p>
          <button className="login-button" onClick={() => { window.location.href = PORTAL_URL; }}>
            Open the portal
          </button>
        </div>
      </div>
    );
  }

  if (!token) {
    return (
      <div className="login-container">
        <div className="login-form">
          <h2>Invitation link incomplete</h2>
          <p>
            This link is missing its invitation code. Ask whoever invited you to send a
            new invitation.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-form">
        <h2>Accept your invitation</h2>
        <p>
          You have been invited to work inside a DBOS environment. You will have your own
          login and your own second factor - nobody in that organisation ever sees your
          password.
        </p>

        {!path && (
          <div className="mode-switch" style={{ marginTop: '24px' }}>
            <button className="login-button" onClick={() => setPath('new')}>
              I am new to DBOS
            </button>
            <button
              className="login-button"
              style={{ marginTop: '12px' }}
              onClick={() => setPath('existing')}
            >
              I already have a DBOS login
            </button>
          </div>
        )}

        {path === 'new' && (
          <form onSubmit={handleNewAccount}>
          <div className="input-group">
            <label htmlFor="inviteEmail">Email Address</label>
            <input
              id="inviteEmail"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
              autoComplete="email"
            />
            <small className="field-hint">
              Use the address the invitation was sent to
            </small>
          </div>

          <div className="input-group">
            <label htmlFor="inviteFullName">Full Name</label>
            <input
              id="inviteFullName"
              type="text"
              placeholder="Your name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
              disabled={loading}
              autoComplete="name"
            />
          </div>

          <div className="input-group">
            <label htmlFor="invitePassword">Password</label>
            <input
              id="invitePassword"
              type="password"
              placeholder="Minimum 8 characters"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
              minLength={8}
              autoComplete="new-password"
            />
          </div>

          <div className="input-group">
            <label htmlFor="inviteConfirmPassword">Confirm Password</label>
            <input
              id="inviteConfirmPassword"
              type="password"
              placeholder="Re-enter your password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              disabled={loading}
              autoComplete="new-password"
            />
          </div>

          {error && <div className="error-message">{error}</div>}

          <button className="login-button" type="submit" disabled={loading}>
            {loading ? 'Setting up...' : 'Accept and continue'}
          </button>
        </form>
        )}

        {path === 'existing' && (
          <form onSubmit={handleExistingAccount}>
          <div className="input-group">
            <label htmlFor="existingEmail">Email Address</label>
            <input
              id="existingEmail"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
              autoComplete="email"
            />
            <small className="field-hint">
              Use the address the invitation was sent to
            </small>
          </div>

          <div className="input-group">
            <label htmlFor="existingPassword">Password</label>
            <input
              id="existingPassword"
              type="password"
              placeholder="Your DBOS password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
              autoComplete="current-password"
            />
          </div>

          {error && <div className="error-message">{error}</div>}

          <button className="login-button" type="submit" disabled={loading}>
            {loading ? 'Adding your seat...' : 'Accept and continue'}
          </button>
        </form>
        )}
      </div>
    </div>
  );
}

export default InviteAccept;
